/**
 * LoopAlerts Component
 *
 * Shows looped products with diagnosis and skip/retry actions.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  SkipForward,
  Lightbulb,
  FileText,
  CheckCircle,
  Search,
} from 'lucide-react';
import {
  getLoops,
  diagnoseLoop,
  skipProduct,
  retryProduct,
  ProductStatus,
  LoopDiagnosis,
  FactualDiscrepancy,
  MissingSpec,
} from '@/services/pipelineService';

interface LoopAlertsProps {
  runId: string;
  siteId: string;
  onAction?: () => void;
}

/**
 * Convert technical recommendations to human-friendly messages
 */
function getHumanFriendlyRecommendation(rec: string | { description?: string; action?: string; priority?: string }): {
  title: string;
  action: string;
  priority: 'high' | 'medium' | 'low';
} {
  const text = typeof rec === 'string' ? rec : rec?.description || '';
  const priority = (typeof rec === 'object' ? rec?.priority : 'medium') as 'high' | 'medium' | 'low';

  // Pattern matching for common technical recommendations
  if (text.toLowerCase().includes('missing spec') || text.toLowerCase().includes('feed metadata')) {
    return {
      title: 'Add Missing Product Information',
      action: 'Go to your product feed and add the missing specifications (like battery life, dimensions, etc.) that customers need to make a purchase decision.',
      priority: 'high'
    };
  }

  if (text.toLowerCase().includes('domain knowledge')) {
    return {
      title: 'Create Domain Knowledge Entry',
      action: 'Go to Domain Knowledge and add an entry with the missing product information. This helps the AI generate accurate content.',
      priority: 'high'
    };
  }

  if (text.toLowerCase().includes('persona') || text.toLowerCase().includes('target audience')) {
    return {
      title: 'Review Audience Settings',
      action: 'Check your site\'s persona settings to ensure they match your actual target customers.',
      priority: 'medium'
    };
  }

  // Default
  return {
    title: 'Review and Fix',
    action: text || 'Review the product data and try regenerating.',
    priority: priority || 'medium'
  };
}

function DiagnosisDialog({
  runId,
  siteId,
  product,
  onSkip,
  onRetry,
}: {
  runId: string;
  siteId: string;
  product: ProductStatus;
  onSkip: (reason: string) => void;
  onRetry: () => void;
}) {
  const [diagnosis, setDiagnosis] = useState<LoopDiagnosis | null>(null);
  const [loading, setLoading] = useState(true);
  const [skipReason, setSkipReason] = useState('');
  const [showSkipForm, setShowSkipForm] = useState(false);

  useEffect(() => {
    const loadDiagnosis = async () => {
      try {
        const data = await diagnoseLoop(runId, product.productId);
        setDiagnosis(data);
      } catch (error) {
        console.error('Failed to load diagnosis:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDiagnosis();
  }, [runId, product.productId]);

  if (loading) {
    return (
      <DialogContent>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          Why This Product Is Stuck
        </DialogTitle>
        <DialogDescription>{product.productName}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Summary Box */}
        <div className="p-4 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
          <p className="text-sm text-orange-700 dark:text-orange-300">
            <strong>The system tried {product.iterations} times</strong> to generate content for this product,
            but it kept failing quality checks. Here's what's happening and how to fix it:
          </p>
        </div>

        {/* SPECIFIC FACTUAL DISCREPANCIES - The key improvement */}
        {diagnosis?.factualDiscrepancies && diagnosis.factualDiscrepancies.length > 0 && (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2 text-red-700 dark:text-red-300">
              ❌ Factual Errors Found
            </h4>
            <p className="text-xs text-red-600 dark:text-red-400 mb-3">
              The AI generated incorrect values that don't match your product feed data:
            </p>
            <div className="space-y-2">
              {diagnosis.factualDiscrepancies.map((d: FactualDiscrepancy, index: number) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    d.severity === 'CRITICAL'
                      ? 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700'
                      : d.severity === 'HIGH'
                      ? 'bg-orange-100 dark:bg-orange-900 border-orange-300 dark:border-orange-700'
                      : 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <span className="font-mono text-sm font-medium capitalize">
                        {d.attribute.replace(/_/g, ' ')}
                      </span>
                      <Badge
                        variant="outline"
                        className={`ml-2 text-xs ${
                          d.severity === 'CRITICAL'
                            ? 'border-red-500 text-red-600'
                            : d.severity === 'HIGH'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-yellow-500 text-yellow-600'
                        }`}
                      >
                        {d.severity}
                      </Badge>
                    </div>
                    {d.canAutoFix && (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        Can Auto-Fix
                      </Badge>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-red-600 dark:text-red-400 line-through">
                      Generated: {d.generated}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      Actual: {d.actual}
                    </span>
                  </div>
                  {d.feedDataQuality && d.feedDataQuality !== 'OK' && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ Feed data quality: {d.feedDataQuality}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {diagnosis.autoFixSummary?.canAutoFix && (
              <div className="mt-3 p-2 bg-green-100 dark:bg-green-900 rounded border border-green-300 dark:border-green-700">
                <p className="text-sm text-green-700 dark:text-green-300">
                  ✅ <strong>{diagnosis.autoFixSummary.autoFixableCount} discrepancies</strong> can be automatically fixed.
                  The correct values from your product feed will be used in the next regeneration.
                </p>
              </div>
            )}
          </div>
        )}

        {/* SPECIFIC MISSING SPECS - The key improvement */}
        {diagnosis?.missingSpecs && diagnosis.missingSpecs.length > 0 && (
          <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2 text-amber-700 dark:text-amber-300">
              📋 Missing Specifications
            </h4>
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
              The content is missing these important product specifications:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {diagnosis.missingSpecs.map((spec: MissingSpec, index: number) => (
                <div
                  key={index}
                  className={`p-2 rounded border ${
                    spec.severity === 'CRITICAL'
                      ? 'bg-red-100 dark:bg-red-900 border-red-300'
                      : spec.severity === 'HIGH'
                      ? 'bg-orange-100 dark:bg-orange-900 border-orange-300'
                      : 'bg-amber-100 dark:bg-amber-900 border-amber-300'
                  }`}
                >
                  <span className="text-sm font-medium capitalize">
                    {spec.name.replace(/_/g, ' ')}
                  </span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {spec.severity}
                  </Badge>
                </div>
              ))}
            </div>
            {diagnosis.autoFixSummary?.canAutoResearch && (
              <div className="mt-3 p-2 bg-purple-100 dark:bg-purple-900 rounded border border-purple-300 dark:border-purple-700">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  🔍 <strong>{diagnosis.autoFixSummary.autoResearchableCount} specs</strong> can be auto-researched.
                  Click "Research Specs" below to find this information automatically.
                </p>
              </div>
            )}
          </div>
        )}

        {/* What's Going Wrong - Human Friendly (fallback for other issues) */}
        {diagnosis?.failurePattern && diagnosis.failurePattern.length > 0 &&
         (!diagnosis?.factualDiscrepancies?.length && !diagnosis?.missingSpecs?.length) && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2">
              ❌ What's Going Wrong
            </h4>
            <ul className="space-y-2 ml-4">
              {diagnosis.failurePattern.map((pattern, index) => (
                <li key={index} className="text-sm text-muted-foreground">
                  • {pattern}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Why It's Happening */}
        {diagnosis?.rootCauses && diagnosis.rootCauses.length > 0 && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2">
              🔍 Why It's Happening
            </h4>
            <ul className="space-y-2 ml-4">
              {diagnosis.rootCauses.map((cause, index) => (
                <li key={index} className="text-sm text-orange-600 dark:text-orange-400">
                  • {cause}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* How To Fix - Human Friendly with Action Buttons */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h4 className="font-medium mb-3 flex items-center gap-2 text-blue-700 dark:text-blue-300">
            <Lightbulb className="h-4 w-4" />
            How To Fix This
          </h4>

          {/* Primary Actions */}
          <div className="space-y-4">
            {/* Research Missing Specs - Link to Enrichment Review */}
            <div className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <span className="font-medium text-purple-700 dark:text-purple-300">
                    Research Missing Specs
                  </span>
                  <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                    Use AI to research and fill in missing product specifications.
                    This adds data directly to the product for better content generation.
                  </p>
                </div>
                <Link
                  to={`/research/enrichment-review?mode=research&productId=${product.productId}&runId=${runId}`}
                  className="shrink-0"
                >
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                    Research Specs →
                  </Button>
                </Link>
              </div>
            </div>

            {/* Add Domain Knowledge */}
            <div className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <span className="font-medium text-blue-700 dark:text-blue-300">
                    Add Domain Knowledge
                  </span>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    Create a knowledge entry with additional context about the product.
                    Use this for brand-specific info or category expertise.
                  </p>
                </div>
                <Link
                  to={`/domain-knowledge?product=${encodeURIComponent(product.productName)}&productId=${product.productId}`}
                  className="shrink-0"
                >
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                    Add Knowledge →
                  </Button>
                </Link>
              </div>
            </div>

            {/* Secondary Actions */}
            {diagnosis?.recommendations && diagnosis.recommendations.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                  Other suggestions:
                </p>
                <ul className="space-y-2">
                  {diagnosis.recommendations.map((rec, index) => {
                    const friendly = getHumanFriendlyRecommendation(rec);
                    // Skip domain knowledge suggestion since we have a button for it
                    if (friendly.title === 'Create Domain Knowledge Entry') return null;
                    return (
                      <li key={index} className="text-sm flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        <span className="text-blue-600 dark:text-blue-400">
                          {friendly.action}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Research Created */}
        {product.researchCreated && product.researchCreated.length > 0 && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Research Created
            </h4>
            <ul className="space-y-1">
              {product.researchCreated.map((research, index) => (
                <li key={index} className="text-sm flex items-center gap-2">
                  <FileText className="h-3 w-3 text-muted-foreground" />
                  <span>{research.title}</span>
                  <Badge variant="outline" className="text-xs">
                    {research.type}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Technical Details (Collapsed) */}
        {product.lastError && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              View technical details
            </summary>
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900 border rounded-lg font-mono text-xs">
              {product.lastError}
            </div>
          </details>
        )}

        {/* Skip Form */}
        {showSkipForm && (
          <div className="space-y-2 p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
            <label className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Why are you skipping this product?
            </label>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              This helps us improve the system for similar products in the future.
            </p>
            <Textarea
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder="e.g., Product data is incomplete, Will fix in next feed update, Not a priority product..."
              rows={3}
              className="bg-white dark:bg-gray-900"
            />
          </div>
        )}
      </div>

      <DialogFooter className="flex-col sm:flex-row gap-2">
        {showSkipForm ? (
          <>
            <Button variant="outline" onClick={() => setShowSkipForm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => onSkip(skipReason)}
              disabled={!skipReason.trim()}
            >
              <SkipForward className="h-4 w-4 mr-1" />
              Skip This Product
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1 text-left text-xs text-muted-foreground hidden sm:block">
              <strong>Skip:</strong> Remove from this batch<br />
              <strong>Retry:</strong> Try generating again after you fix the data
            </div>
            <Button variant="outline" onClick={() => setShowSkipForm(true)}>
              <SkipForward className="h-4 w-4 mr-1" />
              Skip
            </Button>
            <Button onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry After Fixing
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

export function LoopAlerts({ runId, siteId, onAction }: LoopAlertsProps) {
  const [products, setProducts] = useState<ProductStatus[]>([]);
  const [stats, setStats] = useState<{
    total: number;
    byCategory: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadLoops = async () => {
    setLoading(true);
    try {
      const data = await getLoops(runId);
      setProducts(data.products);
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to load loops:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLoops();
  }, [runId]);

  const handleSkip = async (productId: string, reason: string) => {
    setActionLoading(productId);
    try {
      await skipProduct(runId, productId, reason);
      await loadLoops();
      onAction?.();
    } catch (error) {
      console.error('Failed to skip product:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (productId: string) => {
    setActionLoading(productId);
    try {
      await retryProduct(runId, productId);
      await loadLoops();
      onAction?.();
    } catch (error) {
      console.error('Failed to retry product:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
          <p className="font-medium text-foreground">All Products Processing Normally</p>
          <p className="text-sm">No products are stuck. Great job!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 dark:border-orange-800">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Products That Need Attention
            </CardTitle>
            <CardDescription>
              {stats?.total || products.length} product(s) couldn't pass quality checks after multiple tries
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadLoops}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Helpful Tip */}
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          💡 <strong>Tip:</strong> Click "Diagnose" to see what's wrong and how to fix it.
          You can also skip products that aren't a priority right now.
        </div>

        {/* Category Breakdown */}
        {stats?.byCategory && Object.keys(stats.byCategory).length > 0 && (
          <div className="flex gap-2 mb-4 flex-wrap">
            {Object.entries(stats.byCategory).map(([category, count]) => (
              <Badge key={category} variant="outline">
                {category}: {count}
              </Badge>
            ))}
          </div>
        )}

        {/* Product List */}
        <div className="space-y-2">
          {products.map((product) => (
            <div
              key={product.productId}
              className="p-3 border border-orange-200 dark:border-orange-800 rounded-lg flex items-center justify-between bg-orange-50/50 dark:bg-orange-950/30"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{product.productName}</div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" />
                    {product.iterations} tries
                  </span>
                  {product.lastError && (
                    <span className="text-red-500 truncate max-w-[200px]">
                      {product.lastError}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Search className="h-4 w-4 mr-1" />
                      Diagnose
                    </Button>
                  </DialogTrigger>
                  <DiagnosisDialog
                    runId={runId}
                    siteId={siteId}
                    product={product}
                    onSkip={(reason) => handleSkip(product.productId, reason)}
                    onRetry={() => handleRetry(product.productId)}
                  />
                </Dialog>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRetry(product.productId)}
                  disabled={actionLoading === product.productId}
                >
                  {actionLoading === product.productId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
